import React, { useMemo, useState, useCallback } from 'react';

type DependenciesPanelProps = {
  files: Record<string, string>;
  onOpenFile?: (path: string) => void;
};

type Dependency = {
  name: string;
  version: string;
  type: 'dependency' | 'devDependency' | 'peerDependency';
};

type PackageJson = {
  name?: string;
  version?: string;
  description?: string;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
  peerDependencies?: Record<string, string>;
};

export const DependenciesPanel = React.memo(function DependenciesPanel({
  files,
  onOpenFile,
}: DependenciesPanelProps) {
  const [filter, setFilter] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    dependencies: true,
    devDependencies: true,
    peerDependencies: false,
  });

  // Parse package.json
  const packageJson = useMemo<PackageJson | null>(() => {
    const content = files['package.json'] || files['/package.json'];
    if (!content) return null;

    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }, [files]);

  // Extract dependencies
  const dependencies = useMemo<Dependency[]>(() => {
    if (!packageJson) return [];

    const deps: Dependency[] = [];

    if (packageJson.dependencies) {
      Object.entries(packageJson.dependencies).forEach(([name, version]) => {
        deps.push({ name, version, type: 'dependency' });
      });
    }

    if (packageJson.devDependencies) {
      Object.entries(packageJson.devDependencies).forEach(([name, version]) => {
        deps.push({ name, version, type: 'devDependency' });
      });
    }

    if (packageJson.peerDependencies) {
      Object.entries(packageJson.peerDependencies).forEach(([name, version]) => {
        deps.push({ name, version, type: 'peerDependency' });
      });
    }

    return deps;
  }, [packageJson]);

  // Filter dependencies
  const filteredDeps = useMemo(() => {
    if (!filter.trim()) return dependencies;
    const lowerFilter = filter.toLowerCase();
    return dependencies.filter(dep =>
      dep.name.toLowerCase().includes(lowerFilter) ||
      dep.version.toLowerCase().includes(lowerFilter)
    );
  }, [dependencies, filter]);

  // Group by type
  const groupedDeps = useMemo(() => {
    const groups: Record<string, Dependency[]> = {
      dependencies: [],
      devDependencies: [],
      peerDependencies: [],
    };

    filteredDeps.forEach(dep => {
      if (dep.type === 'dependency') groups.dependencies.push(dep);
      else if (dep.type === 'devDependency') groups.devDependencies.push(dep);
      else groups.peerDependencies.push(dep);
    });

    return groups;
  }, [filteredDeps]);

  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  const openNpmPage = useCallback((packageName: string) => {
    window.open(`https://www.npmjs.com/package/${packageName}`, '_blank');
  }, []);

  const handleOpenPackageJson = useCallback(() => {
    if (onOpenFile) {
      onOpenFile('package.json');
    }
  }, [onOpenFile]);

  if (!packageJson) {
    return (
      <div className="dependencies-panel" style={{ padding: '12px' }}>
        <div style={{ color: 'var(--muted)', fontSize: '12px', textAlign: 'center' }}>
          No package.json found
        </div>
      </div>
    );
  }

  const renderDependencyList = (deps: Dependency[], sectionName: string, title: string) => {
    if (deps.length === 0) return null;

    const isExpanded = expandedSections[sectionName];

    return (
      <div key={sectionName} style={{ marginBottom: '8px' }}>
        <button
          onClick={() => toggleSection(sectionName)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '6px 8px',
            background: 'var(--bg-secondary)',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            color: 'var(--text)',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          <span style={{
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}>
            ▶
          </span>
          {title}
          <span style={{
            marginLeft: 'auto',
            color: 'var(--muted)',
            fontWeight: 'normal',
          }}>
            {deps.length}
          </span>
        </button>

        {isExpanded && (
          <div style={{ paddingLeft: '8px', marginTop: '4px' }}>
            {deps.map(dep => (
              <div
                key={dep.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '4px 8px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                className="dependency-item"
                onClick={() => openNpmPage(dep.name)}
                title={`Open ${dep.name} on npm`}
              >
                <span style={{
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}>
                  {dep.name}
                </span>
                <span style={{
                  color: 'var(--muted)',
                  fontSize: '11px',
                  marginLeft: '8px',
                  flexShrink: 0,
                }}>
                  {dep.version}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dependencies-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px',
        borderBottom: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
            {packageJson.name || 'Project'}
          </div>
          {packageJson.version && (
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              v{packageJson.version}
            </div>
          )}
        </div>
        <button
          onClick={handleOpenPackageJson}
          className="pill-btn"
          style={{ fontSize: '11px', padding: '4px 8px' }}
          title="Open package.json"
        >
          Edit
        </button>
      </div>

      {/* Search */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
        <input
          type="text"
          placeholder="Filter dependencies..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: '4px',
            color: 'var(--text)',
            fontSize: '12px',
          }}
        />
      </div>

      {/* Dependencies List */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 12px' }}>
        {renderDependencyList(groupedDeps.dependencies, 'dependencies', 'Dependencies')}
        {renderDependencyList(groupedDeps.devDependencies, 'devDependencies', 'Dev Dependencies')}
        {renderDependencyList(groupedDeps.peerDependencies, 'peerDependencies', 'Peer Dependencies')}

        {filteredDeps.length === 0 && filter && (
          <div style={{
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: '12px',
            padding: '20px',
          }}>
            No dependencies matching "{filter}"
          </div>
        )}

        {dependencies.length === 0 && (
          <div style={{
            textAlign: 'center',
            color: 'var(--muted)',
            fontSize: '12px',
            padding: '20px',
          }}>
            No dependencies found
          </div>
        )}
      </div>

      {/* Footer Stats */}
      <div style={{
        padding: '8px 12px',
        borderTop: '1px solid var(--border)',
        fontSize: '11px',
        color: 'var(--muted)',
        display: 'flex',
        justifyContent: 'space-between',
      }}>
        <span>Total: {dependencies.length}</span>
        <span>
          {groupedDeps.dependencies.length} prod / {groupedDeps.devDependencies.length} dev
        </span>
      </div>

      <style>{`
        .dependency-item:hover {
          background: var(--bg-secondary);
        }
      `}</style>
    </div>
  );
});

export default DependenciesPanel;
